import React, { useState, useMemo, useCallback, useEffect } from 'react';
import I18n from 'i18n-js';
import {
  union,
  differenceWith,
  compact,
  reject,
  uniq,
} from 'lodash';
import qs from 'qs';
import { withRouter, WithRouterProps } from 'react-router';
import { QueryResult } from '@apollo/client';

import { LISTING_STATES } from '@reverbdotcom/commons/src/constants';
import experiments from '@reverbdotcom/commons/src/experiments';
import CSPHeader from './components/csp_header';
import CSPJumplinks, {
  JUMPLINK_SIMILAR,
  JUMPLINK_PRICEGUIDE,
  JUMPLINK_BLURB,
  JUMPLINK_LISTINGS,
} from './components/csp_jumplinks';
import { CSPProductModule } from './components/csp_product_module';
import CSPMetadata from './components/csp_metadata';
import {
  CoreCspLayoutQuery,
  CspFiltersAndListingsQuery,
} from '../../schemas/rql';
import { Paths, withWebHost } from '../shared/url_helpers';
import relatedBuyingGuide from '../related_buying_guide';
import CSPFeaturedListing from './components/csp_featured_listing';
import RecentlyViewedCspsRow from '../recently_viewed_csps_row';
import { CSPEmptyState } from './components/csp_empty_state';
import { CSPFeedCallout } from './components/csp_feed_callout';
import AppInstallCallout from '../app_install_callout';
import { CSPProductFamilyCTA } from './components/csp_product_family_cta';
import CSPPriceGuideModuleContainer from '../price_guide_tool/csp_module/csp_price_guide_module_container';
import SellYoursV4 from '../sell_yours_v4';
import { isUSA } from '../../lib/geo';

import SeoBlurb from '@reverbdotcom/commons/src/cms/components/seo_blurb';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import { canViewFinancingOptions } from '@reverbdotcom/commons/src/user_context_helpers';
import { getCategoryAttributesForMparticle } from '@reverbdotcom/commons/src/csp_category_helper';
import SimilarCspsRow from '@reverbdotcom/commons/src/components/similar_csps_row';
import { ListingsCollection as ListingsCollectionClass } from '@reverbdotcom/commons/src/components/listings_collection';
import { MParticlePageName, trackPageView, trackEvent, MParticleEventName, PageViewEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { formatExperimentsForEventAttributes, userContextIsTrackable } from '@reverbdotcom/commons/src/elog/mparticle';
import ScrollTarget from '@reverbdotcom/commons/src/components/scroll_target';
import { trackCriteoProductList } from '@reverbdotcom/commons/src/criteo';
import { trackFacebookEvent, VIEW_CONTENT_EVENT, PRODUCT_CONTENT_TYPE } from '@reverbdotcom/commons/src/facebook';
import { TruncationContainer } from '@reverbdotcom/commons/src/components/truncation_container';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';

import { RCAlertBox } from '@reverbdotcom/cadence/components';
import { isCoreBrandNew } from '@reverbdotcom/commons/src/condition_helpers';
import { connect, EmptyExternalProps } from '@reverbdotcom/commons/src/connect';
import { CSPProviders } from './components/csp_providers';
import CmsPageCards, { CMS2_PAGE } from '@reverbdotcom/commons/src/cms/components/cms_page_cards';
import ProductReviewsLayout from '../ProductReviewsLayout';

import {
  cspFiltersAndListingsQuery,
  getFiltersAndListingsVariables,
  getRootCategorySlug,
  hideBumpedAndSponsoredListings,
} from './queries/csp_filters_and_listings_query';
import { cspLayoutQuery, getLayoutQueryVariables } from './queries/csp_layout_query';
import { CSPFilterRow } from './components/csp_filter_row';
import { useJumplinksState } from './helpers/useJumplinksState';
import { getSponsoredListings } from './helpers/get_sponsored_listings';
import {
  ALL_TOGETHER,
  ALL_BUMPED,
  LISTINGS_LIMIT,
  LISTINGS_GRID_LIMIT,
  LISTING_TRUNCATION_THRESHOLD,
  GRID_EXP_LISTING_TRUNCATION_REM_HEIGHT,
  LISTING_TRUNCATION_REM_HEIGHT,
  SHOW_FEATURED_IN_COLLECTION_LIMIT,
} from './constants';
import { getItem, setItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { LIST_VIEW_STATE } from '@reverbdotcom/commons/src/components/list_view_toggle';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

export type ICSPLayoutProps = WithRouterProps;

export type LayoutQueryResult = QueryResult<CoreCspLayoutQuery.Query>;
export type FiltersAndListingsQueryResult = QueryResult<CspFiltersAndListingsQuery.Query>;

const ALWAYS_TRACKED_EXPERIMENTS = [
  experiments.DUMMY,
  experiments.MV_DUMMY,
  experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
  experiments.PRICE_DROP_FILTER,
  experiments.WEB2_LOOK_AND_FEEL_2,
];
const COMPONENT_NAME = 'CspListings';
const TRUNCATION_CONTAINER_TRACKING_NAME = 'CspShowMoreButton';

export function CSPLayout(props: ICSPLayoutProps) {
  const { location, params } = props;
  const user = useUser();
  const [scrollTarget, setScrollTarget] = useState<ScrollTarget>(null);
  const [showListingRowCardsLayout, setShowListingRowCardsLayout] = useState(false);

  // **** Queries ****
  const layoutQueryResults = useQuery<CoreCspLayoutQuery.Query, CoreCspLayoutQuery.Variables>(
    cspLayoutQuery,
    {
      ssr: true,
      // allows for partial failures in RQL response
      errorPolicy: 'all',
      variables: getLayoutQueryVariables({ ...props, user }),
    },
  );

  const layoutData = useMemo(() => {
    return layoutQueryResults.data ?? layoutQueryResults.previousData ?? {};
  }, [layoutQueryResults]);

  const { hfid, csp } = layoutData ?? {};

  const rootCategorySlug = getRootCategorySlug(csp);

  const filtersAndListingsQueryResults = useQuery<CspFiltersAndListingsQuery.Query, CspFiltersAndListingsQuery.Variables>(
    cspFiltersAndListingsQuery,
    {
      ssr: false,
      // allows for partial failures in RQL response
      errorPolicy: 'all',
      variables: getFiltersAndListingsVariables({ ...props, rootCategorySlug, user }),
    },
  );


  const filtersAndListingsData = useMemo(() => {
    return filtersAndListingsQueryResults.data ?? filtersAndListingsQueryResults.previousData ?? {};
  }, [filtersAndListingsQueryResults]);

  const queryDataLoading = layoutQueryResults?.loading || filtersAndListingsQueryResults?.loading;

  // **** Data Massaging ****

  // Returns Specific Listing from paid if hfid param present or just the featured listing from arbiter if present
  const promotedFeaturedListing = useMemo(() => {
    const buyBoxListing = layoutData?.featuredListing?.listing;

    if (hfid?.state === LISTING_STATES.LIVE) {
      return hfid;
    }

    if (buyBoxListing) {
      return buyBoxListing;
    }

    return null;
  }, [hfid, layoutData?.featuredListing?.listing]);

  // Returns either `allListings` or `allBumped` listings from listings query
  const getListings = useCallback((name: string) => {
    return filtersAndListingsData?.[name]?.listings || [];
  }, [filtersAndListingsData]);

  // Returns hfid or featured listing from arbiter OR first bumped listing OR first regular listing
  const featuredListing = useMemo(() => {
    if (promotedFeaturedListing) { return promotedFeaturedListing; }

    const hasListings = (filtersAndListingsData?.allListings?.total || 0) > 0;

    if (hasListings) {
      return (getListings(ALL_BUMPED).length) ? getListings(ALL_BUMPED)[0] : getListings(ALL_TOGETHER)[0];
    }

    return null;
  }, [promotedFeaturedListing, getListings, filtersAndListingsData]);

  // Returns bumped listings with the featured listing removed if present in bumped listings array
  const bumpedListings = useMemo(() => {
    const listings = getListings(ALL_BUMPED);

    if (!featuredListing) return listings;

    const listingsWithoutFeatured = reject(listings, (listing) => {
      return listing.id === featuredListing.id;
    });

    return listingsWithoutFeatured.slice(0, LISTINGS_LIMIT - 1);
  }, [featuredListing, getListings]);

  const sponsoredListings = useMemo(() => (
    getSponsoredListings({
      cspSponsoredListing: filtersAndListingsData?.cspSponsoredListing,
      featuredListing,
      bumpedListings,
    })
  ), [filtersAndListingsData, bumpedListings, featuredListing]);

  // Returns fallback listings from search for non-US and AU users if there are no listings returned in the regular search
  const fallbackListings = useMemo(() => {
    return filtersAndListingsData?.allListings?.fallbackListings || [];
  }, [filtersAndListingsData?.allListings?.fallbackListings]);

  const allNonBumpedListings = useMemo(() => {
    return [...getListings(ALL_TOGETHER), ...fallbackListings];
  }, [getListings, fallbackListings]);

  const showCspFeaturedInCollection = showListingRowCardsLayout || allNonBumpedListings.length <= SHOW_FEATURED_IN_COLLECTION_LIMIT;

  // allNonBumpedListings with sponsored and the featured listings conditionally removed
  const nonBumpedListings = useMemo(() => {
    const idEqual = (a, b) => (a.id === b.id);

    const listingsToExclude = showCspFeaturedInCollection ?
      sponsoredListings :
      [...sponsoredListings, ...[featuredListing].filter(Boolean)];

    return differenceWith(allNonBumpedListings, listingsToExclude, idEqual);
  }, [sponsoredListings, featuredListing, allNonBumpedListings, showCspFeaturedInCollection,
  ]);

  const { setJumplinks, jumplinks } = useJumplinksState({
    queryDataLoading,
    hfidPresent: !!hfid,
    featuredListingPresent: !!featuredListing,
    location,
    totalNonbumpedListings: nonBumpedListings.length,
    totalBumpedListings: bumpedListings.length,
  });

  const currentNonFeaturedListingsBeingDisplayed = union(sponsoredListings, nonBumpedListings);
  const allCurrentListingsBeingDisplayed = compact(union(currentNonFeaturedListingsBeingDisplayed, [featuredListing]));

  // **** EVENTS ****
  const trackListings = useCallback(() => {
    let trackedExperiments = [
      experiments.DUMMY,
      experiments.MV_DUMMY,
    ];

    if (isUSA(user.countryCode)) {
      trackedExperiments = trackedExperiments.concat([
        experiments.BEST_PRICE_LISTINGS_SORT_FINAL,
        experiments.LIKELIHOOD_TO_SELL_SORT_RESTART,
      ]);
    }

    trackEvent({
      eventName: MParticleEventName.ComponentView,
      entityType: 'CSP',
      entityId: csp?.id,
      listingsCount: allCurrentListingsBeingDisplayed.length,
      componentName: COMPONENT_NAME,
      experiments: formatExperimentsForEventAttributes(user, trackedExperiments),
    });

    const listings = [].concat(featuredListing, bumpedListings, nonBumpedListings);
    const listingIds = uniq(listings.filter(l => !!l).map(l => l.id));

    trackCriteoProductList({
      listingIds,
    }, user);

    trackFacebookEvent(
      VIEW_CONTENT_EVENT,
      {
        content_ids: listingIds,
        content_type: PRODUCT_CONTENT_TYPE,
      },
    );
  }, [csp, allCurrentListingsBeingDisplayed.length, featuredListing, bumpedListings, nonBumpedListings, user]);

  const logView = useCallback(() => {
    if (queryDataLoading) return;

    const trackedExperiments = [...ALWAYS_TRACKED_EXPERIMENTS];
    const formattedUserExperiments = formatExperimentsForEventAttributes(user, trackedExperiments);

    function featuredListingData(): Partial<PageViewEvent> {
      if (featuredListing) {
        return ({
          featuredListingCondition: isCoreBrandNew(featuredListing.condition.conditionUuid) ? 'new' : 'used',
          featuredListingConditionSlug: featuredListing.condition.conditionSlug,
        });
      }

      return {};
    }

    trackPageView({
      pageName: MParticlePageName.CSP,
      cspSlug: params.slug,
      cspId: csp?.id,
      cspHasOverview: !!csp?.summary,
      brand: csp?.brand?.slug,
      finishCount: csp?.finishes?.length,
      listingsCount: allCurrentListingsBeingDisplayed.length,
      listingIds: nonBumpedListings?.map(l => l.id),
      bumpedListingIds: sponsoredListings?.filter(l => l.bumped)?.map(l => l.id),
      sponsoredListingIds: sponsoredListings?.map(l => l.id),
      reviewCount: csp?.reviews?.total,
      ...featuredListingData(),
      experiments: formattedUserExperiments,
      ...getCategoryAttributesForMparticle(csp),
    });

    trackListings();
  }, [
    csp,
    featuredListing,
    allCurrentListingsBeingDisplayed.length,
    sponsoredListings,
    nonBumpedListings,
    params.slug,
    queryDataLoading,
    trackListings,
    user,
  ]);

  // call logView after the query data has finished loading
  useEffect(() => {
    if (!queryDataLoading) {
      logView();
    }
  }, [logView, queryDataLoading]);

  // **** UI RELATED ****
  function setListViewUI(listEnabled:boolean) {
    setItem(LIST_VIEW_STATE, listEnabled);
    setShowListingRowCardsLayout(listEnabled);
    filtersAndListingsQueryResults.refetch({
      limit: listEnabled ? LISTINGS_LIMIT : LISTINGS_GRID_LIMIT,
      includeFullFinancingFields: canViewFinancingOptions(user),
      shouldHideBumpAndSponsoredListings: hideBumpedAndSponsoredListings({
        pageNumber: location.query.page,
        sortQuery: location.query.sort,
      }),
      shouldSkipTracking: !userContextIsTrackable(user),
    });
  }

  useEffect(()=> {
    const listType = getItem(LIST_VIEW_STATE, false);

    if (listType !== showListingRowCardsLayout) {
      setShowListingRowCardsLayout( listType );
    }
  }, [showListingRowCardsLayout]);

  // scroll to the top of the page on initial load and when the slug changes
  useEffect(() => {
    const anchor = window && window.location?.hash?.replace('#', '');
    if (anchor) {
      const element = document.getElementById(anchor);

      if (element) {
        window.scrollTo(0, element.getBoundingClientRect().top + window.scrollY);
      }
    }

    if (!anchor && scrollTarget && scrollTarget.scrollToTop) {
      scrollTarget.scrollToTop();
    }
  }, [scrollTarget, params.slug]);

  const queryForAdditionalFeaturedListingFields = !promotedFeaturedListing;

  const buyingGuideSlug = useMemo(() => {
    if (layoutQueryResults?.loading || !csp) return null;

    return relatedBuyingGuide(
      csp?.brand?.name,
      csp.model,
      (csp?.categories || []),
      user,
    );
  }, [layoutQueryResults?.loading, csp, user]);

  function getListingsTruncationHeight() {
    const count = nonBumpedListings.length + sponsoredListings.length;
    const truncationHeight = showListingRowCardsLayout ? LISTING_TRUNCATION_REM_HEIGHT : GRID_EXP_LISTING_TRUNCATION_REM_HEIGHT;

    if (count > LISTING_TRUNCATION_THRESHOLD) return truncationHeight;
    return null;
  }

  function renderEmptyState() {
    if (showCspFeaturedInCollection && currentNonFeaturedListingsBeingDisplayed.length) return null;
    if (sponsoredListings.length) return null;

    return (
      <CSPEmptyState
        heading={I18n.t('discovery.csp2.notFound.heading')}
        feedPrompt={I18n.t('discovery.csp2.notFound.feedPrompt')}
      />
    );
  }

  const soldListingId = qs.parse(
    location?.search,
    { ignoreQueryPrefix: true },
  ).ended_listing;

  return (
    <CSPProviders>
      <div
        className="dynamic-page dynamic-page--csp"
        itemScope
        itemType="http://schema.org/Product"
      >
        <AppInstallCallout
          calloutLocation="csp"
          canonicalUrl={withWebHost(Paths.csp.expand({ cspSlug: params.slug }))}
        />
        <ScrollTarget onlyScrollUp ref={setScrollTarget} />
        <CSPHeader
          csp={csp}
          slug={params.slug}
          soldListingId={soldListingId as any} // TODO: qs types are broken
        />
        <CSPMetadata
          csp={csp}
          listings={allCurrentListingsBeingDisplayed}
          loading={layoutQueryResults?.loading}
          condition={params.condition}
        />
        <CSPJumplinks
          user={user}
          csp={csp}
          {...jumplinks}
        />
        <CSPFeaturedListing
          listing={featuredListing}
          csp={csp}
          isLoading={layoutQueryResults?.loading}
          isBumped={!!featuredListing?.bumped && !hfid}
          loggedOut={user?.loggedOut}
          queryForAdditionalFields={queryForAdditionalFeaturedListingFields}
        />

        <div className="site-wrapper">
          <SellYoursV4 csp={csp} belowListing />
          <CSPFilterRow
            location={location}
            csp={csp}
            filtersAndListingsQueryResult={filtersAndListingsQueryResults}
            layoutQueryResult={layoutQueryResults}
            queryDataLoading={queryDataLoading}
            setListViewState={setListViewUI}
          />
        </div>
        {!!fallbackListings.length &&
          <div className="mb-4">
            <RCAlertBox type="warning">
              {I18n.t('discovery.csp2.viewingFallbackListings')}
            </RCAlertBox>
          </div>
        }
        <TruncationContainer
          remMaxHeight={getListingsTruncationHeight()}
          trackingName={TRUNCATION_CONTAINER_TRACKING_NAME}
        >
          <ListingsCollectionClass
            jumplinkSlug={JUMPLINK_LISTINGS}
            listings={nonBumpedListings}
            sponsoredListings={sponsoredListings}
            componentName={COMPONENT_NAME}
            loading={filtersAndListingsQueryResults?.loading}
            emptyState={renderEmptyState()}
            hideMetadataAsOffer={!!hfid}
            displayAsRowCards={showListingRowCardsLayout}
            displayAsGrid={!showListingRowCardsLayout}
            displayGreatValue
            showEstimatedMonthlyPayment={canViewFinancingOptions(user)}
            cspFeaturedListingId={featuredListing?.id}
            showLocationOnGridCard={!showListingRowCardsLayout}
            largeTiles={!showListingRowCardsLayout}
            displayAddToCart={!showListingRowCardsLayout}
            displayMakeAnOffer={!showListingRowCardsLayout}
            showShippingDisplay={!showListingRowCardsLayout}
            useNewGrid
            hideActionsOnMobile
          />
          <RQLPagination
            limit={showListingRowCardsLayout ? LISTINGS_LIMIT : LISTINGS_GRID_LIMIT}
            total={filtersAndListingsData?.allListings?.total || 0}
            offset={filtersAndListingsData?.allListings?.offset || 0}
            anchor="listings"
            paginationWindow={2}
            replaceHistory
          />
        </TruncationContainer>
        <div className="site-wrapper">
          {!!currentNonFeaturedListingsBeingDisplayed.length && (
            <CSPFeedCallout />
          )}
          <div className="csp-layout__resources">
            <div className="csp-layout__product-family">
              <CSPProductFamilyCTA
                csp={csp}
              />
            </div>
            <div className="csp-layout__buying-guide">
              {!!buyingGuideSlug && (
                <CmsPageCards
                  pageFields={[
                    { pageSource: CMS2_PAGE, slug: buyingGuideSlug },
                  ]}
                  layoutSection="sidebar"
                />
              )}
            </div>
          </div>
          <CSPProductModule
            csp={csp}
            loading={layoutQueryResults?.loading}
            setJumplinks={setJumplinks}
          />
        </div>
        <SimilarCspsRow
          title={I18n.t('discovery.comparisonShoppingPages.similarCsps')}
          cspSlug={params.slug}
          onRender={() => setJumplinks({ [JUMPLINK_SIMILAR]: true })}
          id={JUMPLINK_SIMILAR}
        />
        <RecentlyViewedCspsRow />
        {!!csp?.id && (
          <CSPPriceGuideModuleContainer
            csp={csp}
            componentId={JUMPLINK_PRICEGUIDE}
          />
        )}
        <div className="site-wrapper">
          <ProductReviewsLayout
            cspId={csp?.id}
            trackingDisplayStyle="csp"
          />
        </div>
        <SeoBlurb
          title={I18n.t('discovery.comparisonShoppingPages.jumpLinks.blurb')}
          content={csp?.body}
          truncationHeight={150}
          setJumplinks={setJumplinks}
          name={JUMPLINK_BLURB}
        />
      </div>
    </CSPProviders>
  );
}

export default connect<EmptyExternalProps, ICSPLayoutProps>([
  withRouter,
])(CSPLayout);
